import { Suspense } from 'react'

import { Link, useNavigate } from '@tanstack/react-router'

import { usePostHog } from '@posthog/react'
import { Bell, ChevronsUpDown, LogOut, Settings } from 'lucide-react'

import { DemoHidden } from '@/features/demo/components/demo-hidden'
import NotificationsCounter from '@/features/notifications/components/counter'
import { useNotificationsPanel } from '@/features/notifications/hooks/use-notification-panel'
import { cn } from '@/utils/ui/styles'

import { useMe } from '@/shared/auth/queries'

import { ErrorBoundary } from '@/components/common/error-boundary'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	useSidebar,
} from '@/components/ui/sidebar'

import { UserAvatar } from './app-user-avatar'

function UserButton() {
	const posthog = usePostHog()

	const {
		data: { user, avatar_url },
	} = useMe()

	const { open: openNotificationsPanel } = useNotificationsPanel()

	const { isMobile, open } = useSidebar()
	const navigate = useNavigate()

	// Set the Sentry user

	const handleLogout = () => {
		posthog.capture('logout')
		posthog.reset()

		navigate({
			to: '/logout',
		})
	}

	return (
		<SidebarMenu>
			<SidebarMenuItem>
				<ErrorBoundary>
					<Suspense fallback={null}>
						<NotificationsCounter
							className={cn({
								'top-0 left-7': open,
								'-top-2 left-5': !open,
							})}
						/>
					</Suspense>
				</ErrorBoundary>

				<DropdownMenu>
					<DropdownMenuTrigger
						render={
							<SidebarMenuButton
								className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground hover:text-sidebar-foreground"
								size="lg"
								data-testid="user-menu-trigger"
							>
								<UserAvatar user={user} avatar={avatar_url ?? ''} />

								<div className="grid flex-1 text-left text-sm leading-tight">
									<span className="truncate font-semibold">
										{user.fname} {user.lname}
									</span>

									<span className="truncate text-xs">{user.uname}</span>
								</div>
								<ChevronsUpDown className="ml-auto size-4" />
							</SidebarMenuButton>
						}
					/>
					<DropdownMenuContent
						className="min-w-56 rounded-lg"
						side={isMobile ? 'bottom' : 'right'}
						align="end"
						sideOffset={4}
					>
						<DropdownMenuGroup>
							<DropdownMenuLabel className="text-sidebar-foreground cursor-default p-0 font-normal">
								<div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
									<UserAvatar user={user} avatar={avatar_url ?? ''} />
									<div className="grid flex-1 text-left text-sm leading-tight">
										<span className="truncate font-semibold">
											{user.fname} {user.lname}
										</span>
										<span className="truncate text-xs">{user.uname}</span>
									</div>
								</div>
							</DropdownMenuLabel>
							<DropdownMenuSeparator />
							{/* Settings is demo-excluded (excluded-routes.ts) — this dropdown
							    was its only entry point in the shell (ticket 03). */}
							<DemoHidden>
								<DropdownMenuGroup>
									<DropdownMenuItem
										render={<Link to="/settings" className="cursor-pointer" />}
									>
										<Settings className="mr-2 h-6 w-6" />
										Settings
									</DropdownMenuItem>
								</DropdownMenuGroup>
							</DemoHidden>
							<DropdownMenuGroup>
								<DropdownMenuItem
									onClick={() => {
										posthog.capture('open_notifications')
										openNotificationsPanel()
									}}
								>
									<ErrorBoundary fallback={() => <></>}>
										<Suspense fallback={null}>
											<NotificationsCounter className="-top-1 left-5" />
										</Suspense>
									</ErrorBoundary>
									<Bell className="mr-2 h-6 w-6" />
									Notifications
								</DropdownMenuItem>
							</DropdownMenuGroup>
							<DropdownMenuSeparator />
							<DropdownMenuItem
								onClick={handleLogout}
								className="cursor-pointer"
							>
								<LogOut className="mr-2 h-6 w-6" />
								Log out
							</DropdownMenuItem>
						</DropdownMenuGroup>
					</DropdownMenuContent>
				</DropdownMenu>
			</SidebarMenuItem>
		</SidebarMenu>
	)
}

export default UserButton
